import React, { useEffect, useState } from "react";
import { LocalTime } from "@js-joda/core";
import { HStack, Input, Button, Box } from "@chakra-ui/react";

type Props = {
  value?: LocalTime;
  onChange?: (value: LocalTime) => void;
};

type Meridian = "AM" | "PM";

export function TimePicker({ value, onChange }: Props) {
  const [hour, setHour] = useState(toInitial(value).hour);
  const [minute, setMinute] = useState(toInitial(value).minute);
  const [meridian, setMeridian] = useState<Meridian>(toInitial(value).meridian as Meridian);
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    if (!value) return;
    const { hour, minute, meridian } = toInitial(value);
    setHour(hour);
    setMinute(minute);
    setMeridian(meridian);
  }, [value]);

  const validateAndEmit = (hour: string, minutes: string, meridian: Meridian) => {
    const hr = parseInt(hour, 10);
    const min = parseInt(minutes, 10);

    const valid = !isNaN(hr) && !isNaN(min) && hr >= 1 && hr <= 12 && min >= 0 && min <= 59;
    setIsInvalid(!valid);

    if (valid) {
      let hour24 = hr % 12;
      if (meridian === "PM") hour24 += 12;
      onChange?.(LocalTime.of(hour24, min));
    }
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
    setHour(val);
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
    setMinute(val);
  };

  const handleBlur = () => {
    const paddedHour = hour.padStart(2, "0");
    const paddedMinute = minute.padStart(2, "0");
    setHour(paddedHour);
    setMinute(paddedMinute);
    validateAndEmit(paddedHour, paddedMinute, meridian);
  };

  const toggleMeridian = () => {
    const newMeridian = meridian === "AM" ? "PM" : "AM";
    setMeridian(newMeridian);
    validateAndEmit(hour.padStart(2, "0"), minute.padStart(2, "0"), newMeridian);
  };

  return (
    <HStack
      bg="white"
      border="1px solid"
      borderColor={isInvalid ? "red.400" : "gray.300"}
      borderRadius="md"
      px={3}
      spacing={2}
    >
      <Input
        inputMode="numeric"
        maxLength={2}
        placeholder="hh"
        size="sm"
        textAlign="center"
        value={hour}
        variant="unstyled"
        width="2.5rem"
        onBlur={handleBlur}
        onChange={handleHourChange}
      />
      <Box>:</Box>
      <Input
        inputMode="numeric"
        maxLength={2}
        placeholder="mm"
        size="sm"
        textAlign="center"
        value={minute}
        variant="unstyled"
        width="2.5rem"
        onBlur={handleBlur}
        onChange={handleMinuteChange}
      />
      <Box bg="gray.300" h="1.25rem" mx={1} w="1px" />
      <Button
        fontWeight="bold"
        minW="unset"
        px={2}
        size="sm"
        variant="ghost"
        onClick={toggleMeridian}
      >
        {meridian}
      </Button>
    </HStack>
  );
}

const toInitial = (time?: LocalTime): { hour: string; minute: string; meridian: Meridian } => {
  const hour = time ? (time.hour() % 12 || 12).toString().padStart(2, "0") : "";
  const minute = time ? time.minute().toString().padStart(2, "0") : "";
  const meridian: Meridian = time ? (time.hour() >= 12 ? "PM" : "AM") : "AM";
  return { hour, minute, meridian };
};
